import React, { useState } from 'react';
import { useLeadIntel } from '../../context/LeadIntelContext';
import { useSequence } from '../../context/SequenceContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import { motion } from 'framer-motion';

const LeadIntelStarterKit = ({ projectId }) => {
  const { addScoringRules, addTriggerRules, clearProjectRules, scoringRules, triggerRules } = useLeadIntel();
  const { sequences } = useSequence();
  const { permissions } = useCRM();
  
  const [industryPack, setIndustryPack] = useState('Standard');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const projectScoring = scoringRules.filter(r => r.project_id === projectId);
  const projectTriggers = triggerRules.filter(r => r.project_id === projectId);

  const handleCreate = () => {
    if (!permissions.canOutreach) {
      alert("Du mangler tilgang til Lead Intelligence (Add-on).");
      return;
    }

    setLoading(true);
    setSuccess(false);

    // 1. Cleanup existing rules
    clearProjectRules(projectId);

    setTimeout(() => {
      // 2. Define Packs
      const scoringBase = { scope: 'Project', project_id: projectId, enabled: true };
      let newScoring = [];

      switch (industryPack) {
        case 'SaaS':
          newScoring = [
            { name: "Tech stack change (SaaS)", when_signal_type: "TechStackChange", when_priority_hint: "*", score_delta: 20, reason: "Stack-endring gir perfekt timing." },
            { name: "Hiring (SaaS)", when_signal_type: "Hiring", when_priority_hint: "*", score_delta: 15, reason: "Vekst = systembehov." },
            { name: "Funding (SaaS)", when_signal_type: "Funding", when_priority_hint: "*", score_delta: 25, reason: "Funding = budsjett." },
            { name: "Product Launch", when_signal_type: "ProductLaunch", when_priority_hint: "*", score_delta: 15, reason: "Skalering." }
          ];
          break;
        case 'HR':
          newScoring = [
            { name: "Hiring spike (HR)", when_signal_type: "Hiring", when_priority_hint: "*", score_delta: 25, reason: "Mange ansettelser = HR-behov." },
            { name: "Leadership change (HR)", when_signal_type: "LeadershipChange", when_priority_hint: "*", score_delta: 15, reason: "Ny leder = nye rutiner." },
            { name: "New Office", when_signal_type: "NewOffice", when_priority_hint: "*", score_delta: 10, reason: "Kultur/Onboarding." }
          ];
          break;
        case 'HMS':
          newScoring = [
            { name: "Regulatory Need", when_signal_type: "RegulatoryNeed", when_priority_hint: "*", score_delta: 40, reason: "Lovkrav er kritisk." },
            { name: "Tender Notice", when_signal_type: "TenderNotice", when_priority_hint: "*", score_delta: 25, reason: "Anbud stiller krav." },
            { name: "Press Mention (Risk)", when_signal_type: "PressMention", when_priority_hint: "*", score_delta: 10, reason: "Mulig tilsyn." }
          ];
          break;
        case 'Konsulent':
          newScoring = [
            { name: "Funding (Consulting)", when_signal_type: "Funding", when_priority_hint: "*", score_delta: 20, reason: "Vekst = behov for hender." },
            { name: "New Office", when_signal_type: "NewOffice", when_priority_hint: "*", score_delta: 10, reason: "Kapasitetsbehov." },
            { name: "Partnership", when_signal_type: "Partnership", when_priority_hint: "*", score_delta: 10, reason: "Nye leveranser." }
          ];
          break;
        default: // Standard
          newScoring = [
            { name: "Funding", when_signal_type: "Funding", when_priority_hint: "*", score_delta: 25, reason: "Kjøpekraft." },
            { name: "Hiring", when_signal_type: "Hiring", when_priority_hint: "*", score_delta: 15, reason: "Vekst." },
            { name: "Leadership Change", when_signal_type: "LeadershipChange", when_priority_hint: "*", score_delta: 15, reason: "Ny beslutningstaker." },
            { name: "Tender Notice", when_signal_type: "TenderNotice", when_priority_hint: "*", score_delta: 35, reason: "Kjøpssignal." }
          ];
      }

      const finalScoring = newScoring.map(r => ({ ...scoringBase, ...r }));
      addScoringRules(finalScoring);

      // 3. Define Triggers & Link to Sequence
      
      // LOOKUP LOGIC: Find the specific Compliance Sequence
      const complianceSeq = sequences.find(s => s.name === 'Compliance – Standard');
      const hasComplianceSeq = !!complianceSeq;

      const triggerBase = { scope: 'Project', project_id: projectId, enabled: true };
      const newTriggers = [
        {
          name: "Hot lead → Kontakt innen 24t",
          when_signal_type: "*",
          when_score_band: "Hot",
          action_type: "CreateTask",
          task_title: "HOT lead: kontakt innen 24t",
          task_notes: "Bruk Playbook → AI Coach for Next Best Action basert på siste signal.",
          cooldown_days: 5
        },
        {
          name: "TenderNotice → AI Coach",
          when_signal_type: "TenderNotice",
          when_score_band: "Any",
          action_type: "AI_CoachEvent",
          task_title: "Anbudsignal: lag personalisert opening",
          task_notes: "AI skal foreslå 2 openings + 1 CTA basert på signalet.",
          cooldown_days: 7
        },
        // LINKED TRIGGER
        {
          name: "RegulatoryNeed → Compliance-sekvens",
          when_signal_type: "RegulatoryNeed",
          when_score_band: "Any",
          action_type: "StartSequence",
          sequence_id: hasComplianceSeq ? complianceSeq.sequence_id : null,
          enabled: hasComplianceSeq, // Safe-fail: Disabled if sequence missing
          task_title: hasComplianceSeq ? "Startet compliance-sekvens" : "MANGLER SEKVENS: Compliance",
          task_notes: hasComplianceSeq 
            ? `Koblet til sekvens: ${complianceSeq.name}. Krever manuell godkjenning av første e-post.` 
            : "Sekvensen 'Compliance – Standard' mangler. Opprett den i Sekvenser for å aktivere denne regelen.",
          cooldown_days: 14
        }
      ];

      const finalTriggers = newTriggers.map(r => ({ ...triggerBase, ...r }));
      addTriggerRules(finalTriggers);

      setLoading(false);
      setSuccess(true);
    }, 800);
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-top-4">
      {/* Setup Card */}
      <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50 rounded-bl-full -mr-16 -mt-16 z-0"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-2xl text-indigo-600">
              <SafeIcon icon="🚀" className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-900">Lead Intel Starter Kit</h3>
              <p className="text-sm text-slate-500">Automatisk oppsett av regler basert på bransje.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Velg Bransje-pakke</label>
              <select 
                value={industryPack} 
                onChange={(e) => setIndustryPack(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Standard">Standard (Generell B2B)</option>
                <option value="SaaS">SaaS / Tech</option>
                <option value="HR">HR & Rekruttering</option>
                <option value="HMS">HMS & Compliance</option>
                <option value="Konsulent">Konsulent / Byrå</option>
              </select>
            </div>
            <button 
              onClick={handleCreate} 
              disabled={loading}
              className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 h-[46px]"
            >
              {loading ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✨" />}
              Opprett Starter Kit
            </button>
          </div>

          {success && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3">
              <SafeIcon icon="✅" className="text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Suksess!</p>
                <p className="text-xs text-emerald-700">Regler opprettet. Triggeren for "RegulatoryNeed" er koblet til sekvensen "Compliance – Standard".</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Results Tables */}
      {(projectScoring.length > 0 || projectTriggers.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Scoring Rules */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h4 className="font-black text-slate-800 text-sm">Scoring Regler</h4>
              <span className="bg-white px-2 py-1 rounded text-xs font-bold border border-slate-200">{projectScoring.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-slate-400 font-black uppercase tracking-widest bg-white">
                  <tr>
                    <th className="px-4 py-3">Navn</th>
                    <th className="px-4 py-3">Signal</th>
                    <th className="px-4 py-3 text-right">Poeng</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {projectScoring.map((rule, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-bold text-slate-700">{rule.name}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono">{rule.when_signal_type}</td>
                      <td className="px-4 py-3 text-right font-black text-emerald-600">+{rule.score_delta}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Trigger Rules */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h4 className="font-black text-slate-800 text-sm">Automasjon (Triggere)</h4>
              <span className="bg-white px-2 py-1 rounded text-xs font-bold border border-slate-200">{projectTriggers.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-slate-400 font-black uppercase tracking-widest bg-white">
                  <tr>
                    <th className="px-4 py-3">Navn</th>
                    <th className="px-4 py-3">Handling</th>
                    <th className="px-4 py-3 text-right">Cooldown</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {projectTriggers.map((rule, i) => (
                    <tr key={i} className={`hover:bg-slate-50 ${!rule.enabled ? 'opacity-50 bg-slate-50' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-bold text-slate-700 block">{rule.name}</span>
                        {!rule.enabled && <span className="text-[9px] text-red-500 font-bold uppercase">Disabled</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {rule.action_type}
                        {rule.sequence_id && <span className="block text-[9px] text-emerald-600 font-bold">Linked to Sequence</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{rule.cooldown_days}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Footer */}
      <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 text-xs text-blue-800 flex gap-3">
        <SafeIcon icon="ℹ️" className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-bold mb-1">Human-in-the-loop Garanti</p>
          <p>Selv om triggere kalles "StartSequence", vil systemet aldri sende meldinger automatisk uten din godkjenning. En oppgave opprettes i din kø for manuell gjennomføring.</p>
        </div>
      </div>
    </div>
  );
};

export default LeadIntelStarterKit;